import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom' // Add useSearchParams
import {
  Grid,
  Snackbar,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useTheme,
  useMediaQuery,
  Box,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
// Import our styled components
import {
  PrimaryButton,
  FormContainer,
  SectionHeader,
} from './ui/StyledComponents'
import supabase from '../supabaseClient'
import dayjs from 'dayjs'; // Import dayjs
import { isAfter } from 'date-fns';
import { useAuth } from '../context/AuthContext'
import { updateCurrentTeacherActivity } from '../utils/teacherActivity'
import DateSelector from './DateSelector'
import AttendanceStatusSelect from './AttendanceStatusSelect'
import StudentQuranProgress from './StudentQuranProgress'
import StudentGreenbookProgress from './StudentGreenbookProgress'
import QuranWeeklyProgress from './QuranWeeklyProgress'
import StudentNotes from './StudentNotes'

function AttendanceForm() {
  const { studentId, classId } = useParams() // Get studentId and classId from the URL
  const navigate = useNavigate() // Initialize the navigate function
  const [searchParams] = useSearchParams()
  const { user, userRole } = useAuth()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [attendanceStatus, setAttendanceStatus] = useState('Present')
  const [date, setDate] = useState(() => {
    const dateParam = searchParams.get('date')
    return dateParam ? dayjs(dateParam) : dayjs()
  })
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState('success') // success or error
  const [student, setStudent] = useState(null) // State for student details
  const [isProgressDialogOpen, setIsProgressDialogOpen] = useState(false)
  const quranRef = useRef();
  const greenbookRef = useRef();

  useEffect(() => {
    // Fetch the student's details (name) based on studentId
    const fetchStudentDetails = async () => {
      const { data, error } = await supabase
        .from('students')
        .select('first_name, last_name, is_quran_student, is_greenbook_student')
        .eq('id', studentId)
        .single() // Fetch a single student by their ID

      if (error) {
        console.error('Error fetching student details:', error)
      } else {
        setStudent(data) // Set the student's details
      }
    }

    // Fetch the student's details
    fetchStudentDetails()
  }, [studentId])

  useEffect(() => {
    // Fetch the student's existing attendance data if it exists
    const fetchAttendance = async () => {
      const { data, error } = await supabase
        .from('attendance')
        .select('*')
        .eq('student_id', studentId)
        .eq('date', date.format('YYYY-MM-DD'))
        .eq('class_id', classId) // Ensure the classId is matched
        .maybeSingle() // Fetch a single record for the student on the selected date

      if (error || !data) {
        setAttendanceStatus('Present') // Default to 'Present' if no attendance is found
      } else {
        setAttendanceStatus(data.status) // Set the attendance status if available
      }
    }

    // Fetch attendance on mount
    fetchAttendance()
  }, [studentId, date, classId]) // Fetch whenever studentId, date, or classId changes

  const saveStudentAttendanceData = async () => {
    const formattedDate = date.format('YYYY-MM-DD')
    const today = new Date()
    const selectedDate = new Date(formattedDate)

    if (isAfter(selectedDate, today)) {
      setSnackbarMessage('Cannot save data for future dates.')
      setSnackbarSeverity('error')
      setOpenSnackbar(true)
      return
    }

    // Save attendance
    const { error: attendanceError } = await supabase.from('attendance').upsert(
      [
        {
          student_id: studentId,
          class_id: classId,
          date: formattedDate,
          status: attendanceStatus,
          updated_at: dayjs().toISOString(),
        },
      ],
      { onConflict: ['student_id', 'date', 'class_id'] },
    )

    if (attendanceError) {
      setSnackbarMessage('Error saving attendance.')
      setSnackbarSeverity('error')
      setOpenSnackbar(true)
      return
    }

    // Save Quran progress if applicable
    let quranError = null
    if (quranRef.current && student?.is_quran_student) {
      const result = await quranRef.current.saveQuranProgress()
      quranError = result?.error
      if (quranError) {
        setSnackbarMessage('Error saving Quran progress.')
        setSnackbarSeverity('error')
        setOpenSnackbar(true)
        return
      }
    }

    // Save Greenbook progress if applicable
    let greenbookError = null
    if (greenbookRef.current && student?.is_greenbook_student) {
      const result = await greenbookRef.current.saveGreenbookProgress()
      greenbookError = result?.error
      if (greenbookError) {
        setSnackbarMessage('Error saving Greenbook progress.')
        setSnackbarSeverity('error')
        setOpenSnackbar(true)
        return
      }
    }

    setSnackbarMessage('Attendance and progress saved successfully!')
    setSnackbarSeverity('success')
    setOpenSnackbar(true)
    
    // Update teacher activity tracking
    await updateCurrentTeacherActivity(user, userRole)
    
    // Navigate back after successful save
    setTimeout(() => {
      navigate(-1); // Go back to previous page
    }, 1000); // Short delay to show success message
  }

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false)
  }

  return (
    <FormContainer>
      <Grid container spacing={{ xs: 2, sm: 2 }}>
        {student && (
          <>
            <Grid item xs={12}>
              <SectionHeader sx={{ 
                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
              }}>
                {student.first_name} {student.last_name}
              </SectionHeader>
            </Grid>

            {/* Status and Date on one row */}
            <Grid item xs={12} container spacing={{ xs: 2, sm: 2 }}>
              <Grid item xs={12} md={6}>
                <AttendanceStatusSelect
                  value={attendanceStatus || 'Present'}
                  onChange={(e) => setAttendanceStatus(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 2, sm: 2 },
                }}>
                  <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' }, minWidth: { xs: '100%', sm: 0 } }}>
                    <DateSelector
                      value={date}
                      onChange={(newDate) => setDate(newDate)}
                    />
                  </Box>
                  <Box sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' }, width: { xs: '100%', sm: 'auto' } }}>
                    <PrimaryButton
                      variant="contained"
                      onClick={() => setIsProgressDialogOpen(true)}
                      fullWidth={isMobile}
                      sx={{ 
                        height: { xs: '56px', sm: '56px' }, // Match height with DateSelector
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        padding: { xs: '10px 16px', sm: '12px 24px' },
                      }}
                    >
                      <AddIcon sx={{ fontSize: { xs: '18px', sm: '20px' }, mr: { xs: 0.5, sm: 1 } }} /> 
                      {isMobile ? 'Add Note' : 'Add Note'}
                    </PrimaryButton>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* Quran Progress and Greenbook Progress side by side */}
            <Grid item xs={12}>
              <Grid container spacing={{ xs: 2, sm: 2 }}>
                {/* Quran Progress Fields */}
                {student?.is_quran_student && (
                  <Grid item xs={12} md={student?.is_greenbook_student ? 5.5 : 12}>
                    <QuranWeeklyProgress 
                      studentId={studentId}
                      classId={classId}
                      selectedDate={date}
                    />
                    <StudentQuranProgress
                      ref={quranRef}
                      studentId={studentId}
                      classId={classId}
                      date={date}
                    />
                  </Grid>
                )}

                {/* Vertical Divider - only show when both sections are present */}
                {student?.is_quran_student && student?.is_greenbook_student && (
                  <Grid item xs={12} md={1} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
                    <Divider orientation="vertical" flexItem sx={{ my: 2 }} />
                  </Grid>
                )}

                {/* Greenbook Progress Fields */}
                {student?.is_greenbook_student && (
                  <Grid item xs={12} md={student?.is_quran_student ? 5.5 : 12}>
                    <StudentGreenbookProgress
                      ref={greenbookRef}
                      studentId={studentId}
                      classId={classId}
                      date={date}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12} sx={{ 
              display: 'flex', 
              justifyContent: { xs: 'stretch', sm: 'flex-end' },
            }}>
              <PrimaryButton 
                variant="contained" 
                onClick={saveStudentAttendanceData}
                fullWidth={isMobile}
                sx={{
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  padding: { xs: '10px 16px', sm: '12px 24px' },
                }}
              >
                Save Progress
              </PrimaryButton>
            </Grid>
          </>
        )}

        {/* Snackbar for success or error */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={5000}
          onClose={handleCloseSnackbar}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
            {snackbarMessage}
          </Alert>
        </Snackbar>

        {/* Progress Notes Dialog */}
        <Dialog
          open={isProgressDialogOpen}
          onClose={() => setIsProgressDialogOpen(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              maxHeight: { xs: '100vh', sm: '90vh' },
              margin: { xs: 0, sm: 2 },
              borderRadius: { xs: 0, sm: 2 },
            },
          }}
        >
          <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, pb: { xs: 1, sm: 2 } }}>
            Add Progress Note
          </DialogTitle>
          <DialogContent sx={{ 
            px: { xs: 2, sm: 3 },
            pb: { xs: 2, sm: 3 },
          }}>
            <StudentNotes
              studentId={studentId}
              showAddNote={true}
            />
          </DialogContent>
          <DialogActions sx={{ 
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 1 },
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            gap: { xs: 1, sm: 0 },
          }}>
            <Button 
              onClick={() => setIsProgressDialogOpen(false)} 
              color="primary"
              fullWidth={isMobile}
              sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </FormContainer>
  )
}

export default AttendanceForm
